import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';

import { SiriProviderShellComponent } from './siri-provider-shell.component';
import { ProviderFeedsBase } from '../shared/provider-feeds-base';
import { ProviderConfig } from '../models/provider.model';
import { Grid } from '../../../shared/components/grid/grid';
import { GridPage } from '../../../shared/components/grid/grid-page';
import { GridCellDirective } from '../../../shared/components/grid/directives/grid-cell.directive';
import { GridColumn, gridColumns } from '../../../shared/components/grid/models/grid.model';
import { LabelBadge, LabelBadgeDot } from '../../../shared/components/label-badge/label-badge';
import { FeedDiagnostics, SiriDiagnosticsPayload } from '../../../shared/models/feed-diagnostics.model';
import { deriveFeedHealth, FeedHealth } from '../util/feed-diagnostics.util';
import { relativeTime } from '../../../shared/utils/relative-time';

const HEALTH_DOT: Record<FeedHealth['status'], LabelBadgeDot | null> = {
  'no-snapshot': null,
  failing: 'red',
  'not-reporting': 'orange',
  ok: 'green',
};

/**
 * SIRI Feeds tab (/admin/providers/siri/:providerId). Adds the SIRI-specific
 * Endpoint/Flavor columns, a Stop health column and the health summary strip,
 * all joined from the SIRI shell's one diagnostics fetch.
 */
@Component({
  selector: 'app-siri-provider-feeds',
  standalone: true,
  imports: [Grid, GridPage, LabelBadge, GridCellDirective, TranslocoDirective],
  templateUrl: './siri-provider-feeds.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block h-full min-h-0' },
})
export class SiriProviderFeedsComponent extends ProviderFeedsBase {
  private readonly siriShell = inject(SiriProviderShellComponent);

  readonly diagnostics = this.siriShell.diagnostics;
  readonly isDiagnosticsLoading = this.siriShell.isDiagnosticsLoading;

  protected override searchableText(feed: ProviderConfig): string[] {
    return [feed.configuration?.['endpoint'] ?? ''];
  }

  /** Derived health per feed, joined from the shell's one admin diagnostics
   *  call. Snapshot is only siri-shaped (the admin call is fetched with
   *  providerType=siri), so this is safe to treat as SiriDiagnosticsPayload. */
  health(feed: ProviderConfig): FeedHealth {
    const snapshot = this.diagnostics().get(feed.id) as FeedDiagnostics<SiriDiagnosticsPayload> | undefined;
    return deriveFeedHealth(snapshot ?? null);
  }

  healthDot(feed: ProviderConfig): LabelBadgeDot | null {
    return HEALTH_DOT[this.health(feed).status];
  }

  healthLabel(feed: ProviderConfig): string {
    return this.translateHealthLabel(this.health(feed));
  }

  snapshotAge(feed: ProviderConfig): string {
    const snapshot = this.diagnostics().get(feed.id);
    return snapshot ? relativeTime(snapshot.generatedAt) : '—';
  }

  private translateHealthLabel(health: FeedHealth): string {
    const t = (k: string, params?: Record<string, unknown>) => this.transloco.translate(`providers.diagnostics.${k}`, params);
    switch (health.status) {
      case 'no-snapshot':
        return t('noSnapshot');
      case 'failing':
        return t(health.error === 1 ? 'stopFailing' : 'stopsFailing', { count: health.error });
      case 'not-reporting':
        return t(health.unknown === 1 ? 'stopNotReporting' : 'stopsNotReporting', { count: health.unknown });
      case 'ok':
        return t('allStopsOk');
    }
  }

  /** Provider-level roll-ups for the summary strip, derived from per-feed
   *  health. Judgement calls, called out inline: error takes precedence over
   *  unknown (a feed with both errors and unknowns counts only as "with
   *  errors"), and "not reporting" mixes never-published feeds together with
   *  feeds that have some unknown stops but no errors — two different
   *  situations bucketed together for a simple three-way summary. */
  readonly summary = computed(() => {
    const diagnostics = this.diagnostics();
    let allOk = 0;
    let withErrors = 0;
    let notReporting = 0;
    let newestGeneratedAt: string | null = null;
    let oldestGeneratedAt: string | null = null;
    for (const feed of this.shell.feeds()) {
      const health = this.health(feed);
      if (health.status === 'failing') withErrors++;
      else if (health.status === 'no-snapshot' || health.status === 'not-reporting') notReporting++;
      else allOk++;

      const snapshot = diagnostics.get(feed.id);
      if (snapshot) {
        if (!newestGeneratedAt || snapshot.generatedAt > newestGeneratedAt) newestGeneratedAt = snapshot.generatedAt;
        if (!oldestGeneratedAt || snapshot.generatedAt < oldestGeneratedAt) oldestGeneratedAt = snapshot.generatedAt;
      }
    }
    return {
      allOk,
      withErrors,
      notReporting,
      newestAge: newestGeneratedAt ? relativeTime(newestGeneratedAt) : null,
      oldestAge: oldestGeneratedAt ? relativeTime(oldestGeneratedAt) : null,
    };
  });

  readonly columns = computed<GridColumn<ProviderConfig>[]>(() => {
    this.languageService.current(); // re-evaluate on language switch
    const t = (k: string) => this.transloco.translate(`providers.feedsGrid.columns.${k}`);
    return gridColumns<ProviderConfig>([
      ...this.leadingColumns(),
      { key: 'endpoint', label: t('endpoint'), type: 'text', sortable: false, value: (row) => row.configuration?.['endpoint'] ?? '' },
      { key: 'flavor', label: t('flavor'), type: 'template', template: 'flavor', width: 170, sortable: false },
      this.statusColumn(),
      { key: 'stopHealth', label: t('stopHealth'), type: 'template', template: 'stopHealth', width: 190, sortable: false },
    ]);
  });
}
